import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import {
  SchemaFieldIcon,
  SchemaFieldIconType,
  SchemaFieldIconVariant,
} from '@rossum/components/schema-field-icon/SchemaFieldIcon';
import {
  IconDotsVertical,
  IconEdit,
  IconInfoCircle,
  IconRefresh,
  IconTrash,
} from '@rossum/ui/icons/tabler';
import { Box, IconButton, Menu, SvgIcon, useTheme } from '@rossum/ui/material';
import { NonAdminRestrictor } from '../../../../../components/Restrictors';
import { isEmbedded } from '../../../../../constants/config';
import {
  batchDatapointDelete,
  deleteDatapoint,
  setPendingValidationForFormulaDatapoint,
  updateDatapointValue,
} from '../../../../../redux/modules/datapoints/actions';
import { datapointPathSelector } from '../../../../../redux/modules/datapoints/selector';
import { useBackToDocument } from '../../back-to-document/useBackToDocument';
import {
  isEditableFormulaField,
  isEditedFormulaField,
} from '../../formula-helpers';
import { Item, ItemWithValue } from '../utils';
import { ContextActionItem } from './ContextActionItem';
import { useFieldDetailsDialog } from './useFieldDetailsDialog';

type ItemContextActionsProps = {
  item: Item;
  active: boolean;
  disabled: boolean;
  iconProps: {
    type: SchemaFieldIconType;
    variant: SchemaFieldIconVariant;
    color: 'success' | 'inherit' | 'aurora';
  };
  fieldSettingsPath: string | null;
};

// TODO: @sidebarV2 resolve name and props - can we derive them all if we pass whole `item`?
export const ItemContextActions = ({
  active,
  iconProps,
  fieldSettingsPath,
  item,
  disabled,
}: ItemContextActionsProps) => {
  const intl = useIntl();
  const theme = useTheme();

  const [hovered, setHovered] = useState(false);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleMouseEnter = useCallback(() => {
    setHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (!anchorEl) {
      setHovered(false);
    }
  }, [anchorEl]);

  const handleOpenMenu: React.MouseEventHandler<HTMLButtonElement> =
    useCallback(e => {
      e.stopPropagation();
      setHovered(true);
      setAnchorEl(e.currentTarget);
    }, []);

  // TODO: @sidebarV2 eg. when user opens Field details dialog,
  // the datapoint is selected with consequent click on backdrop or in the dialog
  const handleCloseMenu = useCallback(() => {
    setHovered(false);
    setAnchorEl(null);
  }, []);

  const dispatch = useDispatch();

  const { triggerBackToDocumentToast } = useBackToDocument();

  const datapointPath = useSelector(datapointPathSelector);

  const onReconnectFormulaField = useCallback(
    (itemToReconnect: ItemWithValue) => {
      handleCloseMenu();
      dispatch(setPendingValidationForFormulaDatapoint(itemToReconnect.id));
      dispatch(
        updateDatapointValue(
          {
            id: itemToReconnect.id,
            index: itemToReconnect.meta.datapointIndex,
            validationSource: 'human',
            oldValue: itemToReconnect.value,
            reason: 'reconnect-formula-field',
            noRecalculation: false,
          },
          ''
        )
      );
    },
    [dispatch, handleCloseMenu]
  );

  // TODO: should we reuse `handleDatapointChange` from DocumentSidebar with different `reason`?
  const onDeleteValue = useCallback(
    (itemToDelete: ItemWithValue) => {
      if (itemToDelete.kind === 'sidebar-multivalue-child-field') {
        // user can use this function while not being on the datapoint.
        // In other words `itemToDelete !== activeDatapoint` is possible.
        const correctPath = datapointPath.map((id, index) =>
          index === 2 ? itemToDelete.id : id
        );
        dispatch(deleteDatapoint(correctPath));
      }
      handleCloseMenu();
      dispatch(
        batchDatapointDelete({
          tuplesToDelete: [],
          datapointsToReset: [
            {
              id: itemToDelete.id,
              index: itemToDelete.meta.datapointIndex,
            },
          ],
        })
      );
    },
    [datapointPath, dispatch, handleCloseMenu]
  );

  const { openFieldDetailsDialog, fieldDetailsAvailable, fieldDetailsDialog } =
    useFieldDetailsDialog(item);

  const handleFieldDetailsDialogOpen = useCallback(() => {
    handleCloseMenu();
    openFieldDetailsDialog();
  }, [openFieldDetailsDialog, handleCloseMenu]);

  // TODO: Fix this hotfix
  const atLeastOneOption =
    ('value' in item &&
      !disabled &&
      isEditableFormulaField(item.valueSource, item.editing)) ||
    (fieldSettingsPath && !isEmbedded()) ||
    fieldDetailsAvailable ||
    ('value' in item && !disabled);

  return (
    <>
      <Box
        color="text.secondary"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={e => {
          e.stopPropagation();
        }}
      >
        {hovered && atLeastOneOption ? (
          // TODO: @sidebarV2 extract to separate component?
          <>
            <IconButton
              size="small"
              color="secondary"
              onClick={handleOpenMenu}
              sx={{
                position: 'absolute',
                top: active ? '11px' : '4px',
                left: '11px',
              }}
            >
              <SvgIcon fontSize="medium">
                <IconDotsVertical />
              </SvgIcon>
            </IconButton>
            <Menu
              open={!!anchorEl}
              anchorEl={anchorEl}
              onClose={handleCloseMenu}
            >
              {'value' in item &&
                !disabled &&
                isEditableFormulaField(item.valueSource, item.editing) && (
                  <ContextActionItem
                    icon={
                      <SvgIcon fontSize="medium">
                        <IconRefresh />
                      </SvgIcon>
                    }
                    onClick={() => onReconnectFormulaField(item)}
                    disabled={
                      !isEditedFormulaField(
                        item.valueSource,
                        'noRecalculation' in item
                          ? item.noRecalculation
                          : undefined
                      )
                    }
                  >
                    {intl.formatMessage({
                      id: 'components.sidebarV2.itemContextActions.restoreFormulaValue',
                    })}
                  </ContextActionItem>
                )}
              {fieldSettingsPath && !isEmbedded() && (
                <NonAdminRestrictor>
                  <ContextActionItem
                    icon={
                      <SvgIcon fontSize="medium">
                        <IconEdit />
                      </SvgIcon>
                    }
                    component={RouterLink}
                    onClick={triggerBackToDocumentToast}
                    to={fieldSettingsPath}
                  >
                    {intl.formatMessage({
                      id: 'components.sidebarV2.itemContextActions.editInFieldsSettings',
                    })}
                  </ContextActionItem>
                </NonAdminRestrictor>
              )}

              {fieldDetailsAvailable && (
                <ContextActionItem
                  icon={
                    <SvgIcon fontSize="medium">
                      <IconInfoCircle />
                    </SvgIcon>
                  }
                  onClick={handleFieldDetailsDialogOpen}
                >
                  {intl.formatMessage({
                    id: 'components.sidebarV2.itemContextActions.showFieldDetails',
                  })}
                </ContextActionItem>
              )}

              {'value' in item && !disabled && (
                <ContextActionItem
                  icon={
                    <SvgIcon fontSize="medium">
                      <IconTrash />
                    </SvgIcon>
                  }
                  onClick={() => onDeleteValue(item)}
                  disabled={
                    item.kind !== 'sidebar-multivalue-child-field' &&
                    item.value === ''
                  }
                >
                  {intl.formatMessage({
                    id: 'components.sidebarV2.itemContextActions.deleteValue',
                  })}
                </ContextActionItem>
              )}
            </Menu>
          </>
        ) : (
          <SchemaFieldIcon
            type={iconProps.type}
            variant={iconProps.variant}
            fontSize="medium"
            sx={{
              display: 'block',
              ...(iconProps.color !== 'inherit'
                ? { color: theme.palette[iconProps.color].main }
                : {}),
            }}
            data-cy={`annotation-sidebar-item-icon-${iconProps.color}`}
          />
        )}
      </Box>
      {fieldDetailsDialog}
    </>
  );
};

ItemContextActions.displayName = 'ItemContextActions';
